import { useState, useEffect, useRef } from 'react';
import { ReactComponent as Logo } from '../../images/logo.svg';
import NavLinks from './NavLinks';
import { HashLink } from 'react-router-hash-link';
import i18n from '../../i18n';
import enFlag from '../../images/en.webp';
import ruFlag from '../../images/ru.webp';
import trFlag from '../../images/tr.webp';

const NavBar = () => {
  const [top, setTop] = useState(!window.scrollY);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  const [langOpen, setLangOpen] = useState(false);
  const langMenuRef = useRef(null);

  const languages = {
    en: { label: 'English', flag: enFlag },
    ru: { label: 'Русский', flag: ruFlag },
    tr: { label: 'Türkçe', flag: trFlag },
  };

  const handleClick = () => setIsOpen(!isOpen);

  const handleLangChange = (lng) => {
    i18n.changeLanguage(lng);
    setSelectedLang(lng);
    setLangOpen(false);
  };

  useEffect(() => {
    const scrollHandler = () => setTop(window.pageYOffset <= 10);
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-30 transition duration-300 ease-in-out mb-16 ${!top && 'bg-white shadow-lg'}`}>
      <div className="flex flex-row justify-between items-center py-4 md:px-12 md:mx-12 h-20">
        <div className="flex items-center font-semibold">
          <HashLink smooth to="/#hero">
            <Logo className="h-[142px] w-auto block" />
          </HashLink>
        </div>

        <div className="group flex flex-col items-center">
          <button className="p-2 rounded-lg lg:hidden text-blue-900" onClick={handleClick}>
            <svg className="h-6 w-6 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              {isOpen ? (
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                />
              )}
            </svg>
          </button>

          {/* Desktop Navigation + Language Switcher */}
          <div className="hidden lg:flex items-center space-x-6 p-5">
            <NavLinks />
            <div className="relative inline-block text-left" ref={langMenuRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="p-2 border rounded-full hover:bg-gray-100"
              >
                <img src={languages[selectedLang].flag} alt={selectedLang} className="w-7 h-7 rounded-full" />
              </button>

              {langOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  {Object.entries(languages).map(([lng, { label, flag }]) =>
                    lng !== selectedLang ? (
                      <button
                        key={lng}
                        onClick={() => handleLangChange(lng)}
                        className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-gray-100"
                      >
                        <img src={flag} alt={lng} className="w-5 h-5 rounded-full" />
                        <span>{label}</span>
                      </button>
                    ) : null
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`fixed transition-transform duration-300 ease-in-out transit flex justify-center left-0 w-full h-auto rounded-md p-24 bg-white lg:hidden shadow-xl top-14 ${
              isOpen ? 'block' : 'hidden'
            }`}
          >
            <div className="flex flex-col space-y-6">
              <NavLinks />
              {/* Mobile Language Switcher */}
              <div className="relative text-left" ref={langMenuRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="p-2 border rounded-full hover:bg-gray-100"
                >
                  <img src={languages[selectedLang].flag} alt={selectedLang} className="w-6 h-6 rounded-full" />
                </button>

                {langOpen && (
                  <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    {Object.entries(languages).map(([lng, { label, flag }]) =>
                      lng !== selectedLang ? (
                        <button
                          key={lng}
                          onClick={() => handleLangChange(lng)}
                          className="w-full px-4 py-2 flex items-center space-x-2 hover:bg-gray-100"
                        >
                          <img src={flag} alt={lng} className="w-5 h-5 rounded-full" />
                          <span>{label}</span>
                        </button>
                      ) : null
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
